/**
 * The customer's Earthworks invoice, opened from its "Pay now" link with no
 * login (EW-6b, fieldiq-api `GET /invoice-pay/:token`). The customer reads
 * the invoice and, when the tenant's Stripe is connected, pays the balance or
 * part of it by card.
 *
 * PUBLIC. Every request goes through `publicApiFetch` (api/public.ts), which
 * never sends a staff token, and the path is in lib/public-routes.ts so a
 * stale staff session cannot bounce the reader to /login — the same rules as
 * QuoteReviewPage.
 *
 * THE API DECIDES. Every figure is the invoice's as stored; nothing here adds
 * a line up. The button shows only when the API says `canPayByCard` (sent,
 * still owing, Stripe connected). A part amount is checked here for the
 * reader's sake (ewPayAmountVerdict); the API's INVALID_AMOUNT /
 * AMOUNT_EXCEEDS_BALANCE still decide. Coming back with ?payment=success does
 * NOT mean paid — only the signed webhook records a payment — so the page says
 * it is being confirmed and re-reads the invoice a few times.
 */
import { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle, CreditCard, Loader2 } from 'lucide-react';
import { publicApiFetch, getPublicFetchMessage } from '../api/public';
import { ewDay, fmtMoney, fmtNumber } from '../lib/earthworks';
import { ewPayAmountVerdict } from '../lib/ew-invoices';
import type { EwInvoicePayPublic } from '../types/ew-invoices';

const INPUT = 'w-40 border rounded-lg px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500';
/** After Stripe sends the reader back: re-read the invoice at these delays (ms) while the webhook lands. */
export const PAY_CONFIRM_REREADS = [3000, 8000, 15000];

export default function InvoicePayPage() {
  const { token } = useParams<{ token: string }>();
  const [inv, setInv] = useState<EwInvoicePayPublic | null>(null);
  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<'full' | 'part'>('full');
  const [amountText, setAmountText] = useState('');
  const [paying, setPaying] = useState(false);
  const [searchParams] = useSearchParams();
  const returned = searchParams.get('payment'); // 'success' | 'cancelled' | null — Stripe's redirect back

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const res = await publicApiFetch(`/invoice-pay/${token}`);
      const body = await res.json().catch(() => ({}));
      if (res.status === 404) setInvalid(true);
      else if (!res.ok) setError(body?.error || 'The invoice could not be loaded. Please try again.');
      else setInv(body.data as EwInvoicePayPublic);
    } catch (err) {
      if (!quiet) setError(getPublicFetchMessage(err, 'Unable to connect. Please try again.'));
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [token]);

  useEffect(() => { void load(); }, [load]);

  // Back from Checkout: the webhook may land a moment later. Re-read quietly.
  useEffect(() => {
    if (returned !== 'success') return undefined;
    const timers = PAY_CONFIRM_REREADS.map((ms) => setTimeout(() => { void load(true); }, ms));
    return () => timers.forEach(clearTimeout);
  }, [returned, load]);

  const balance = inv?.balanceDue ?? null;
  const verdict = mode === 'full' ? null : ewPayAmountVerdict(amountText, balance);
  const verdictError = verdict && 'error' in verdict ? verdict.error : null;

  const pay = async () => {
    setError(null);
    if (verdict && 'error' in verdict) { setError(verdict.error); return; }
    setPaying(true);
    try {
      const res = await publicApiFetch(`/invoice-pay/${token}/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(verdict && 'amount' in verdict ? { amount: verdict.amount } : {}),
      });
      const out = await res.json().catch(() => ({}));
      if (res.ok && out?.data?.url) {
        window.location.assign(out.data.url);
        return; // leaving the page; keep the spinner
      }
      if (res.status === 409) await load(true); // paid meanwhile, or no longer payable: show what is true now
      setError(out?.error || 'The card payment page could not be opened. Please try again.');
    } catch (err) {
      setError(getPublicFetchMessage(err, 'Unable to connect. Please try again.'));
    }
    setPaying(false);
  };

  if (loading && !inv) {
    return (
      <div className="min-h-full bg-gray-50 flex items-center justify-center" data-testid="invoice-pay-loading">
        <Loader2 className="w-8 h-8 text-amber-600 animate-spin" />
      </div>
    );
  }

  if (invalid || (!inv && error)) {
    return (
      <div className="min-h-full bg-gray-50">
        <div className="max-w-3xl mx-auto px-6 py-12">
          <div className="bg-white rounded-xl shadow-sm border p-8 text-center" data-testid="invoice-pay-gone">
            <AlertTriangle className="w-14 h-14 text-amber-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-gray-900 mb-2">{invalid ? 'Link not valid' : 'Something went wrong'}</h1>
            <p className="text-gray-600">{invalid ? 'This invoice link is not valid. Please ask for a new one.' : error}</p>
          </div>
        </div>
      </div>
    );
  }
  if (!inv) return null;

  const cur = inv.currency;
  const money = (v: number | null | undefined) => fmtMoney(v, cur);
  const accent = inv.company.primaryColor || '#1e293b';
  const paid = inv.status === 'paid';
  const owing = balance !== null && balance > 0;
  const holdback = (inv.holdbackWithheld ?? 0) > 0;
  const deposit = (inv.depositApplied ?? 0) > 0;

  return (
    <div className="min-h-full bg-gray-50" data-testid="invoice-pay-page">
      <div className="bg-white border-b px-6 py-4" style={{ borderTop: `4px solid ${accent}` }}>
        <div className="max-w-5xl mx-auto flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {inv.company.logoUrl && <img src={inv.company.logoUrl} alt="" className="h-9" />}
            <div>
              <h1 className="font-bold text-gray-900">{inv.company.name ?? '—'}</h1>
              <p className="text-sm text-gray-500">Invoice {inv.invoiceNumber}</p>
            </div>
          </div>
          {inv.company.phone && <p className="text-sm text-gray-600">{inv.company.phone}</p>}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-6 space-y-4">
        {paid && (
          <div data-testid="invoice-pay-paid" className="rounded-xl border p-5 flex items-start gap-3 bg-green-50 border-green-200">
            <CheckCircle className="w-6 h-6 text-green-600 shrink-0" />
            <p className="font-semibold text-gray-900">Paid in full. Thank you.</p>
          </div>
        )}
        {!paid && returned === 'success' && (
          <p className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900" data-testid="invoice-pay-confirming">
            Thank you. Your card payment is being confirmed — it may take a moment to show on this invoice.
          </p>
        )}
        {!paid && returned === 'cancelled' && (
          <p className="text-sm text-gray-600" data-testid="invoice-pay-cancelled">The card payment was cancelled. Nothing was charged.</p>
        )}

        <div className="bg-white rounded-xl shadow-sm border p-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div><p className="text-gray-500">Billed to</p><p className="font-semibold text-gray-900">{inv.customer.name ?? '—'}</p></div>
            <div><p className="text-gray-500">Invoice date</p><p className="font-semibold text-gray-900">{ewDay(inv.invoiceDate)}</p></div>
            <div><p className="text-gray-500">Due</p><p className="font-semibold text-gray-900">{ewDay(inv.dueDate)}</p></div>
            <div><p className="text-gray-500">Balance due</p><p className="font-bold text-lg text-gray-900" data-testid="invoice-pay-balance">{money(balance)}</p></div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-left">
                <tr>
                  <th className="px-4 py-2 font-medium">Description</th>
                  <th className="px-4 py-2 font-medium">Date</th>
                  <th className="px-4 py-2 font-medium">Unit</th>
                  <th className="px-4 py-2 font-medium text-right">Qty</th>
                  <th className="px-4 py-2 font-medium text-right">Rate</th>
                  <th className="px-4 py-2 font-medium text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {inv.lines.map((l, i) => (
                  <tr key={i} className="border-t" data-testid="invoice-pay-line">
                    <td className="px-4 py-2 text-gray-900">{l.description}{l.drawPct !== null && l.drawPct !== 100 ? ` (${fmtNumber(l.drawPct)}%)` : ''}</td>
                    <td className="px-4 py-2 text-gray-600">{l.workDate ? ewDay(l.workDate) : '—'}</td>
                    <td className="px-4 py-2 text-gray-600">{l.unit || '—'}</td>
                    <td className="px-4 py-2 text-right text-gray-900">{fmtNumber(l.quantity)}</td>
                    <td className="px-4 py-2 text-right text-gray-900">{money(l.rate)}</td>
                    <td className="px-4 py-2 text-right font-medium text-gray-900">{money(l.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <dl className="border-t px-4 py-3 text-sm text-gray-900 space-y-1 sm:ml-auto sm:max-w-sm" data-testid="invoice-pay-totals">
            <div className="flex justify-between gap-4"><dt className="text-gray-500">Work</dt><dd data-testid="invoice-pay-work">{money(inv.workSubtotal)}</dd></div>
            {holdback && (
              <div className="flex justify-between gap-4" data-testid="invoice-pay-holdback">
                <dt className="text-gray-500">{inv.holdbackLabel || 'Holdback'} withheld{inv.holdbackPct !== null ? ` (${fmtNumber(inv.holdbackPct)}%)` : ''}</dt>
                <dd>−{money(inv.holdbackWithheld)}</dd>
              </div>
            )}
            <div className="flex justify-between gap-4"><dt className="text-gray-500">Subtotal</dt><dd>{money(inv.subtotal)}</dd></div>
            {inv.taxes.map((t) => (
              <div key={t.label} className="flex justify-between gap-4">
                <dt className="text-gray-500">{t.label}{t.percent !== null ? ` (${fmtNumber(t.percent)}%)` : ''}</dt><dd>{money(t.amount)}</dd>
              </div>
            ))}
            {deposit && (
              <div className="flex justify-between gap-4" data-testid="invoice-pay-deposit">
                <dt className="text-gray-500">Deposit applied</dt><dd>−{money(inv.depositApplied)}</dd>
              </div>
            )}
            <div className="flex justify-between gap-4 font-bold"><dt>Total</dt><dd data-testid="invoice-pay-total">{money(inv.total)}</dd></div>
            <div className="flex justify-between gap-4"><dt className="text-gray-500">Paid</dt><dd data-testid="invoice-pay-paid-amount">{money(inv.amountPaid)}</dd></div>
            <div className="flex justify-between gap-4 font-bold"><dt>Balance due</dt><dd>{money(balance)}</dd></div>
          </dl>
        </div>

        {!paid && owing && (
          <div className="bg-white rounded-xl shadow-sm border p-5 text-sm text-gray-700 space-y-3" data-testid="invoice-pay-box">
            {inv.canPayByCard ? (
              <>
                <div className="flex flex-wrap items-center gap-3">
                  <label className="flex items-center gap-2">
                    <input type="radio" name="pay-mode" checked={mode === 'full'} onChange={() => { setMode('full'); setError(null); }} data-testid="invoice-pay-mode-full" />
                    The balance, {money(balance)}
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" name="pay-mode" checked={mode === 'part'} onChange={() => { setMode('part'); setError(null); }} data-testid="invoice-pay-mode-part" />
                    Part of it
                  </label>
                  {mode === 'part' && (
                    <input
                      aria-label="Amount to pay"
                      inputMode="decimal"
                      type="number"
                      min={0.01}
                      step={0.01}
                      max={balance ?? undefined}
                      value={amountText}
                      onChange={(e) => setAmountText(e.target.value)}
                      placeholder={`up to ${balance !== null ? balance.toFixed(2) : '—'}`}
                      className={INPUT}
                    />
                  )}
                </div>
                {mode === 'part' && verdictError && amountText.trim() !== '' && (
                  <p className="text-amber-800" data-testid="invoice-pay-amount-error">{verdictError}</p>
                )}
                <button type="button" disabled={paying || !!verdictError} onClick={pay} data-testid="invoice-pay-now"
                        className="inline-flex items-center gap-2 rounded-lg bg-amber-500 px-4 py-2 font-semibold text-white hover:bg-amber-600 disabled:opacity-50">
                  {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                  {mode === 'full' ? `Pay ${money(balance)} now` : 'Pay now'}
                </button>
              </>
            ) : (
              <p data-testid="invoice-pay-no-card">Please pay the way your invoice describes.</p>
            )}
          </div>
        )}
        {error && <p role="alert" className="text-sm text-red-700" data-testid="invoice-pay-error">{error}</p>}
      </div>
    </div>
  );
}
